import { FunctionComponent, memo, SyntheticEvent, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Case, Game } from '~types';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import { useGames, useStatusGame } from './hooks';

import GameChange from '~components/game/GameChange';
import { SteamItemImage } from '~components/item';
import Currency from '~components/Currency';
import { Button, Price } from '~ui/index';
import { useKeyPressEvent } from 'react-use';
import { useHover, useConfig } from '~components/hooks';
import IconStatus1 from '../../scss/images/icon-profile-3.svg?react';
import IconStatus2 from '../../scss/images/icon-profile-4.svg?react';

interface IWinItems {
  getCase: Case;
}

interface IItemProps {
  getGame: Game;
  onSellGame: (gameId: number) => void;
}

const Item: FunctionComponent<IItemProps> = memo(({ getGame, onSellGame }) => {
  const [refSellButon, isHoverSell] = useHover();
  const { t } = useTranslation();
  const getItem = getGame?.getItem;
  const getConfig = useConfig();

  const onDone = () => {
    onSellGame(getGame.id);
  };

  return (
    <div className="case-play-win-img-more-item" data-status={getGame.status}>
      <div
        className={`in-case-item status-${getGame.status} ${
          isHoverSell && 'in-case-item--hover-sell'
        }`}
        data-rar={getItem.color}
        data-audio="5.wav"
        data-volume="0.4"
      >
        <div className="in-case-status-default">
          <div className="in-case-status-default-icon">
            {getGame?.status === 1 ? (
              <IconStatus1 />
            ) : (
              getGame?.status === 2 && <IconStatus2 />
            )}
          </div>
          <div className="in-case-status-text">
            {getGame?.status === 1 ? t('Waiting for decision') : t('Sold')}
          </div>
        </div>
        <div className="in-case-item-wrap">
          <div className="in-case-cost">
            <span className="old-sale">
              {getConfig.isEU ? (
                <>
                  <Currency />
                  <Price sum={getGame.price} />
                </>
              ) : (
                <>
                  <Price sum={getGame.price} />
                  <Currency />
                </>
              )}
            </span>
            <span>
              {getConfig.isEU ? (
                <>
                  <Currency />
                  <Price sum={getGame.priceSell} />
                </>
              ) : (
                <>
                  <Price sum={getGame.priceSell} />
                  <Currency />
                </>
              )}
            </span>
          </div>
          <div className="in-case-item-bg"></div>
          <div className="img">
            <SteamItemImage
              src={getItem.getImage}
              alt={getItem.getName}
              size="350x350"
            />
          </div>
          <div className="in-case-info">
            <div className="in-case-item-name">
              {getItem.getName} {getItem?.quality && `- ${getItem?.quality}`}
            </div>
            {getGame.chance && (
              <div className="drop-chance">{getGame.chance}%</div>
            )}
          </div>
        </div>
      </div>
      <div className="case-play-win-img-more-item-btns">
        <GameChange
          ref={refSellButon}
          className="btn btn-sell"
          onDone={onDone}
          getGame={getGame}
          type={0}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="42"
            height="42"
            viewBox="0 0 42 42"
          >
            <path
              d="M1992,1385.667l16-16h-8V1367h13.333v13.333h-2.667v-8l-16,16Z"
              transform="translate(-1981.333 -1356.333)"
              fill="#fff"
            ></path>
          </svg>
          <span>{t('Sell')}</span>
        </GameChange>
        <Link
          className="btn-game btn-game--contracts"
          to="/contract"
          state={{ gameIds: [getGame.id] }}
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21.25 35H18.75V32.5C15.8336 32.2885 13.087 31.0484 11 29.0002C8.95169 26.9134 7.71128 24.1665 7.5 21.25H5V18.75H7.5C7.71128 15.8335 8.95169 13.0866 11 10.9998C13.0873 8.95199 15.8337 7.71192 18.75 7.5V5H21.25V7.5C24.1663 7.71203 26.9127 8.95208 29 10.9998C31.0484 13.0866 32.2888 15.8335 32.5 18.75H35V21.25H32.5C32.2884 24.1664 31.048 26.9132 29 29.0002C26.9127 31.0479 24.1663 32.288 21.25 32.5V35ZM21.25 28.75V30C23.5215 29.8506 25.6614 28.8808 27.2711 27.2711C28.8808 25.6614 29.8506 23.5215 30 21.25H28.75V18.75H30C29.8503 16.4786 28.8804 14.3391 27.2708 12.7295C25.6611 11.1199 23.5215 10.1497 21.25 10V11.25H18.75V10C16.4783 10.1489 14.3381 11.1185 12.7283 12.7283C11.1186 14.3381 10.1489 16.4783 10 18.75H11.25V21.25H10C10.1471 23.5224 11.1161 25.6634 12.7263 27.2736C14.3365 28.8837 16.4776 29.8529 18.75 30V28.75H21.25ZM20 26.25C18.3621 26.1881 16.8081 25.5095 15.6492 24.3506C14.4902 23.1916 13.8119 21.6378 13.75 20C13.8119 18.3621 14.4902 16.8084 15.6492 15.6494C16.8081 14.4905 18.3621 13.8119 20 13.75C21.6378 13.8119 23.1919 14.4905 24.3508 15.6494C25.5098 16.8084 26.1881 18.3621 26.25 20C26.1881 21.6378 25.5098 23.1916 24.3508 24.3506C23.1919 25.5095 21.6378 26.1881 20 26.25ZM20 16.25C19.0054 16.25 18.0516 16.6454 17.3483 17.3486C16.6451 18.0519 16.25 19.0054 16.25 20C16.25 20.9946 16.6451 21.9481 17.3483 22.6514C18.0516 23.3546 19.0054 23.75 20 23.75C20.9946 23.75 21.9484 23.3546 22.6516 22.6514C23.3549 21.9481 23.75 20.9946 23.75 20C23.75 19.0054 23.3549 18.0519 22.6516 17.3486C21.9484 16.6454 20.9946 16.25 20 16.25Z"
              fill="white"
            ></path>
          </svg>
          <span className="text">{t('add_to_contract')}</span>
        </Link>
      </div>
    </div>
  );
});

export const WinItems: FunctionComponent<IWinItems> = memo(({ getCase }) => {
  const [statusGame, setStatusGame] = useStatusGame();
  const getConfig = useConfig();

  useKeyPressEvent(
    ({ code }) => code === 'KeyS',
    () => {
      const button = document.querySelector(
        '.case-play-win .button[data-event="onSellAllItem"]',
      );

      if (button instanceof HTMLElement) {
        button.click();
      }
    },
  );

  useKeyPressEvent(
    ({ code }) => code === 'KeyR',
    () => {
      const button = document.querySelector('[data-event="onPlayAgain"]');

      if (button instanceof HTMLElement) {
        button.click();
      }
    },
  );

  useKeyPressEvent(
    ({ code }) => code === 'KeyI',
    () => {
      const button = document.querySelector('[data-event="goProfile"]');

      if (button instanceof HTMLElement) {
        button.click();
      }
    },
  );

  useKeyPressEvent(
    ({ code }) => code === 'KeyC',
    () => {
      const button = document.querySelector('[data-event="goContract"]');

      if (button instanceof HTMLElement) {
        button.click();
      }
    },
  );

  const [openCases, setGames] = useGames();
  const { t } = useTranslation();

  const onSellGame = (gameId: number) => {
    setGames(openCases =>
      openCases.map(openCase => {
        if (openCase.getGame.id === gameId) {
          openCase.getGame.status = 2;
        }
        return openCase;
      }),
    );
  };

  const onSellAllGames = () => {
    setGames(openCases =>
      openCases.map(openCase => {
        openCase.getGame.status = 2;

        return openCase;
      }),
    );
  };

  const onSetGame = (event: SyntheticEvent) => {
    if (event) {
      event.preventDefault();
    }

    setStatusGame(0);
  };

  const getGames = _.map(openCases, 'getGame');

  const filterGames = useMemo(
    () => _.filter(getGames, getGame => getGame.status === 1),
    [getGames],
  );
  const sumPrice = useMemo(() => _.sumBy(filterGames, 'price'), [filterGames]);
  const sumPriceSell = useMemo(
    () => _.sumBy(filterGames, 'priceSell'),
    [filterGames],
  );
  const gameIds = useMemo(() => _.map(filterGames, 'id'), [filterGames]);

  const chunk = _.chunk(getGames, 5);

  if (statusGame !== 4) {
    return null;
  }

  if (_.size(getGames) < 2) {
    return null;
  }

  return (
    <div className="case-play-win case-play-win-more">
      <div className="case-item" data-rar={getCase.color}>
        {_.map(chunk, (getGames, index) => (
          <div className="case-play-win-img-more" key={index}>
            {_.map(getGames, getGame => (
              <Item
                getGame={getGame}
                key={getGame.id}
                onSellGame={onSellGame}
              />
            ))}
          </div>
        ))}
        <div className="case-play-win-btns">
          <Button
            className="btn-game btn-game--repeat"
            onClick={onSetGame}
            data-event="onPlayAgain"
          >
            <div className="btn-hoykey">r</div>
            <span className="icon material-icons" data-icon="&#xe627;"></span>
            <span className="text">{t('PLAY.PLAY_MORE')}</span>
          </Button>
          {filterGames.length > 0 && (
            <GameChange
              className="btn btn-sell"
              gameIds={gameIds}
              type={2}
              onDone={onSellAllGames}
              sumPriceSell={sumPriceSell}
              sumPrice={sumPrice}
              data-event="onSellAllItem"
            >
              <div className="btn-hoykey">s</div>
              <div className="btn-sale-cost-wrap">
                <div className="btn-sale-cost">
                  {getConfig.isEU ? (
                    <>
                      <Currency />
                      <Price sum={sumPrice} />
                    </>
                  ) : (
                    <>
                      <Price sum={sumPrice} />
                      <Currency />
                    </>
                  )}
                </div>

                <div className="btn-sale-cost percent">+3%</div>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="42"
                height="42"
                viewBox="0 0 42 42"
              >
                <path
                  d="M1992,1385.667l16-16h-8V1367h13.333v13.333h-2.667v-8l-16,16Z"
                  transform="translate(-1981.333 -1356.333)"
                  fill="#fff"
                ></path>
              </svg>
              <span className="text">{t('Sell all')}</span>
              <div className="cost">
                {getConfig.isEU ? (
                  <>
                    <Currency />
                    <Price sum={sumPriceSell} />
                  </>
                ) : (
                  <>
                    <Price sum={sumPriceSell} />
                    <Currency />
                  </>
                )}
              </div>
            </GameChange>
          )}
          {filterGames.length > 0 && (
            <Link
              className="btn-game btn-game--contracts"
              to={{ pathname: '/contract' }}
              state={{ gameIds }}
              data-event="goContract"
            >
              <div className="btn-hoykey">c</div>
              <div className="icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="42"
                  height="42"
                  viewBox="0 0 42 42"
                >
                  <path
                    d="M17.334,32H14.667V29.334A13.162,13.162,0,0,1,6.4,25.6a13.157,13.157,0,0,1-3.737-8.264H0V14.667H2.667A13.16,13.16,0,0,1,6.4,6.4a13.167,13.167,0,0,1,8.262-3.737V0h2.667V2.666A13.167,13.167,0,0,1,25.6,6.4a13.158,13.158,0,0,1,3.736,8.264H32v2.666H29.333A13.162,13.162,0,0,1,25.6,25.6a13.167,13.167,0,0,1-8.264,3.738V32Zm0-6.666v1.332a10.011,10.011,0,0,0,9.334-9.334H25.334V14.667h1.334a10.013,10.013,0,0,0-9.334-9.333V6.666H14.667V5.333a10.009,10.009,0,0,0-9.332,9.333H6.667v2.666H5.337a10,10,0,0,0,9.329,9.334V25.336ZM16,22.667A6.93,6.93,0,0,1,9.334,16,6.93,6.93,0,0,1,16,9.334,6.93,6.93,0,0,1,22.667,16,6.93,6.93,0,0,1,16,22.667ZM16,12a4,4,0,1,0,4,4A4,4,0,0,0,16,12Z"
                    transform="translate(5.333 5.333)"
                    fill="#fff"
                    stroke="rgba(0,0,0,0)"
                    strokeMiterlimit="10"
                    strokeWidth="1"
                  ></path>
                </svg>
              </div>
              <span className="text">
                {getGames.length > 1
                  ? t('add_to_contract_all')
                  : t('add_to_contract')}
              </span>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
});
